import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Search,
  X,
  Check,
  FileText,
  Tag
} from 'lucide-react';

export default function ProcedimentosPage() {
  const { isAdmin } = useAuth();
  const [procedimentos, setProcedimentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProc, setEditingProc] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    sigla: '',
    tipo: 'Exame',
    codigo_sigtap: '',
    categoria: '',
    permite_multipla_selecao: false,
    somente_admin_geral: false,
    preparo: '',
    ativo: true,
    dias_semana: [0, 1, 2, 3, 4, 5, 6]  // Padrao: todos os dias marcados
  });

  useEffect(() => {
    fetchProcedimentos();
  }, []);

  const fetchProcedimentos = async () => {
    try {
      const response = await api.get('/procedimentos');
      const flat = [];
      for (const grupo of response.data) {
        const sigla = grupo.sigla;
        const tipo = grupo.tipo || 'Exame';
        const categoria = grupo.categoria || sigla;
        const origin = grupo.origin || 'hospital';
        for (const p of (grupo.procedimentos || [])) {
          flat.push({
            id: p.id,
            sigla,
            nome: p.nome,
            tipo,
            categoria,
            origin,
            codigo_sigtap: p.codigo || '',
            preparo: p.preparo || '',
            ativo: p.ativo !== false,
            unico_no_dia: !!p.unico_no_dia,
            somente_admin_geral: !!p.somente_admin_geral,
            permite_multipla_selecao: !!grupo.permite_multipla_selecao,
            dias_semana: p.dias_semana || null,
            fonte: p.fonte || 'custom',
          });
        }
      }
      setProcedimentos(flat);
    } catch (error) {
      console.error('Erro ao carregar procedimentos:', error);
      toast.error('Erro ao carregar procedimentos');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUnicoNoDia = async (proc) => {
    const novoValor = !proc.unico_no_dia;
    try {
      await api.patch('/procedimentos/flag-unico-no-dia', {
        sigla: proc.sigla,
        nome: proc.nome,
        codigo: proc.codigo_sigtap || '',
        unico_no_dia: novoValor,
      });
      toast.success(`"${proc.nome}" ${novoValor ? 'marcado' : 'desmarcado'} como Único no dia`);
      fetchProcedimentos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar flag');
    }
  };

  const toggleDiaSemana = (dia) => {
    setFormData((prev) => {
      const cur = prev.dias_semana || [];
      const novo = cur.includes(dia) ? cur.filter((d) => d !== dia) : [...cur, dia].sort();
      return { ...prev, dias_semana: novo };
    });
  };

  const isLabMunSigla = (sigla) => sigla === 'LAB-MUN' || sigla === 'LACEM';

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome || !formData.sigla || !formData.tipo) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }

    try {
      // 1. Salva dados gerais (cadastro/edicao)
      const payload = { ...formData };
      // dias_semana so faz sentido para Lab Municipal
      if (!isLabMunSigla(payload.sigla)) {
        delete payload.dias_semana;
      }

      if (editingProc) {
        await api.put(`/procedimentos/custom/${editingProc.id}`, payload);
      } else {
        await api.post('/procedimentos/custom', payload);
      }

      // 2. Para LAB Municipal, persiste tambem via PATCH /dias-semana
      // (cobre o caso de edicao de hardcoded onde o PUT cria o override mas
      //  o usuario quer flag persistida mesmo sem outras mudancas)
      if (isLabMunSigla(formData.sigla)) {
        const dsAll = (formData.dias_semana || []).length === 7;
        await api.patch('/procedimentos/dias-semana', {
          sigla: formData.sigla,
          nome: formData.nome,
          codigo: formData.codigo_sigtap || '',
          dias_semana: dsAll ? null : formData.dias_semana,
        });
      }

      toast.success(editingProc ? 'Procedimento atualizado com sucesso!' : 'Procedimento cadastrado com sucesso!');
      setShowModal(false);
      setEditingProc(null);
      resetForm();
      fetchProcedimentos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar procedimento');
    }
  };

  const handleEdit = (proc) => {
    setEditingProc(proc);
    // dias_semana: null/undef = todos os 7 dias marcados
    const ds = (proc.dias_semana && proc.dias_semana.length > 0) ? proc.dias_semana : [0, 1, 2, 3, 4, 5, 6];
    setFormData({
      nome: proc.nome || '',
      sigla: proc.sigla || '',
      tipo: proc.tipo || 'Exame',
      codigo_sigtap: proc.codigo_sigtap || '',
      categoria: proc.categoria || '',
      permite_multipla_selecao: proc.permite_multipla_selecao || false,
      somente_admin_geral: proc.somente_admin_geral || false,
      preparo: proc.preparo || '',
      ativo: proc.ativo !== false,
      dias_semana: ds
    });
    setShowModal(true);
  };

  const handleDelete = async (proc) => {
    const isHc = proc.fonte === 'hardcoded';
    const acao = isHc ? 'desativar' : 'excluir';
    const msg = isHc
      ? `Deseja desativar o procedimento "${proc.nome}"? (Sera ocultado do sistema mas pode ser reativado depois)`
      : `Deseja realmente excluir o procedimento "${proc.nome}"?`;
    if (!window.confirm(msg)) {
      return;
    }

    try {
      const resp = await api.delete(`/procedimentos/custom/${proc.id}`);
      const sucessoMsg = resp.data?.deactivated ? 'Procedimento desativado com sucesso!' : 'Procedimento excluído com sucesso!';
      toast.success(sucessoMsg);
      fetchProcedimentos();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Erro ao ${acao} procedimento`);
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      sigla: '',
      tipo: 'Exame',
      codigo_sigtap: '',
      categoria: '',
      permite_multipla_selecao: false,
      somente_admin_geral: false,
      preparo: '',
      ativo: true,
      dias_semana: [0, 1, 2, 3, 4, 5, 6]
    });
  };

  const openNewModal = () => {
    setEditingProc(null);
    resetForm();
    setShowModal(true);
  };

  const filteredProcedimentos = procedimentos.filter(proc => 
    proc.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    proc.sigla?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    proc.categoria?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Agrupar por sigla
  const groupedProcedimentos = filteredProcedimentos.reduce((acc, proc) => {
    const sigla = proc.sigla || 'OUTROS';
    if (!acc[sigla]) acc[sigla] = [];
    acc[sigla].push(proc);
    return acc;
  }, {});

  if (!isAdmin()) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-slate-500">Acesso restrito a administradores</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Procedimentos</h1>
          <p className="text-slate-500 mt-1">Gerencie os procedimentos disponíveis para marcação</p>
        </div>
        <Button onClick={openNewModal} className="bg-teal-700 hover:bg-teal-800">
          <Plus className="h-4 w-4 mr-2" />
          Novo Procedimento
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Buscar por nome, sigla ou categoria..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="search-procedimentos"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Total</p>
          <p className="text-2xl font-bold text-slate-900">{procedimentos.length}</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Ativos</p>
          <p className="text-2xl font-bold text-teal-600">{procedimentos.filter(p => p.ativo !== false).length}</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Exames</p>
          <p className="text-2xl font-bold text-blue-600">{procedimentos.filter(p => p.tipo === 'Exame').length}</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Consultas</p>
          <p className="text-2xl font-bold text-violet-600">{procedimentos.filter(p => p.tipo === 'Consulta').length}</p>
        </div>
      </div>

      {/* Lista de Procedimentos */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
        </div>
      ) : Object.keys(groupedProcedimentos).length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">Nenhum procedimento cadastrado</h3>
          <p className="text-slate-500 mb-4">Cadastre procedimentos customizados para usar no sistema</p>
          <Button onClick={openNewModal} className="bg-teal-700 hover:bg-teal-800">
            <Plus className="h-4 w-4 mr-2" />
            Cadastrar Procedimento
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedProcedimentos).sort().map(([sigla, procs]) => (
            <div key={sigla} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                <div className="flex items-center gap-2">
                  <Tag className="h-4 w-4 text-teal-600" />
                  <span className="font-bold text-slate-900">{sigla}</span>
                  <span className="text-sm text-slate-500">({procs.length} procedimento{procs.length !== 1 ? 's' : ''})</span>
                </div>
              </div>
              <div className="divide-y divide-slate-100">
                {procs.map((proc) => (
                  <div key={proc.id} className={`p-4 transition-colors ${proc.ativo ? 'hover:bg-slate-50' : 'bg-slate-50/60 opacity-60'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-slate-900">{proc.nome}</h3>
                          {!proc.ativo && (
                            <span className="px-2 py-0.5 text-xs bg-slate-200 text-slate-600 rounded">Inativo</span>
                          )}
                          {proc.permite_multipla_selecao && (
                            <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">Multipla Selecao</span>
                          )}
                          {proc.somente_admin_geral && (
                            <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded font-medium">Somente Admin Geral</span>
                          )}
                          {proc.dias_semana && proc.dias_semana.length > 0 && proc.dias_semana.length < 7 && (
                            <span
                              className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded font-medium"
                              title="Dias permitidos para marcacao"
                              data-testid={`dias-semana-badge-${proc.id}`}
                            >
                              {proc.dias_semana.map((d) => ['D','S','T','Q','Q','S','S'][d]).join('')}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                          <span>Tipo: {proc.tipo}</span>
                          {proc.codigo_sigtap && <span>SIGTAP: {proc.codigo_sigtap}</span>}
                          {proc.categoria && <span>Categoria: {proc.categoria}</span>}
                        </div>
                        {proc.preparo && (
                          <div className="mt-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                            <span className="font-semibold">Preparo:</span> {proc.preparo}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <label
                          className="flex items-center gap-2 text-xs font-medium text-slate-600 px-3 py-1.5 rounded-lg border border-slate-200 hover:border-rose-300 hover:bg-rose-50 cursor-pointer transition-colors"
                          title="Marcar como 'Único no dia': só pode ser agendado 1x por dia no sistema inteiro"
                          data-testid={`toggle-unico-${proc.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={!!proc.unico_no_dia}
                            onChange={() => handleToggleUnicoNoDia(proc)}
                            className="h-4 w-4 rounded border-slate-300 text-rose-600 focus:ring-rose-500"
                          />
                          Único no dia
                        </label>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(proc)}
                          data-testid={`edit-proc-${proc.id}`}
                          title="Editar procedimento"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        {proc.fonte !== 'hardcoded' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(proc)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-proc-${proc.id}`}
                            title="Remover procedimento customizado"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                        {proc.fonte === 'hardcoded' && proc.ativo && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(proc)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`deactivate-proc-${proc.id}`}
                            title="Desativar procedimento (sera ocultado do sistema)"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de Cadastro/Edição */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900">
                {editingProc ? 'Editar Procedimento' : 'Novo Procedimento'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <Label htmlFor="nome">Nome do Procedimento *</Label>
                <Input
                  id="nome"
                  value={formData.nome}
                  onChange={(e) => setFormData({...formData, nome: e.target.value})}
                  placeholder="Ex: Hemograma Completo"
                  required
                  data-testid="proc-nome-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="sigla">Sigla *</Label>
                  <Input
                    id="sigla"
                    value={formData.sigla}
                    onChange={(e) => setFormData({...formData, sigla: e.target.value.toUpperCase()})}
                    placeholder="Ex: LAB"
                    required
                    data-testid="proc-sigla-input"
                  />
                </div>

                <div>
                  <Label htmlFor="tipo">Tipo *</Label>
                  <Select
                    value={formData.tipo}
                    onValueChange={(value) => setFormData({...formData, tipo: value})}
                  >
                    <SelectTrigger data-testid="proc-tipo-select">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Exame">Exame</SelectItem>
                      <SelectItem value="Consulta">Consulta</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="codigo_sigtap">Código SIGTAP</Label>
                  <Input
                    id="codigo_sigtap"
                    value={formData.codigo_sigtap}
                    onChange={(e) => setFormData({...formData, codigo_sigtap: e.target.value})}
                    placeholder="Ex: 02.02.01.001-0"
                    data-testid="proc-sigtap-input"
                  />
                </div>

                <div>
                  <Label htmlFor="categoria">Categoria</Label>
                  <Input
                    id="categoria"
                    value={formData.categoria}
                    onChange={(e) => setFormData({...formData, categoria: e.target.value})}
                    placeholder="Ex: LABORATORIAIS"
                    data-testid="proc-categoria-input"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-3 py-2">
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.permite_multipla_selecao}
                      onChange={(e) => setFormData({...formData, permite_multipla_selecao: e.target.checked})}
                      className="w-4 h-4 text-teal-600 rounded border-slate-300 focus:ring-teal-500"
                      data-testid="proc-multipla-checkbox"
                    />
                    <span className="text-sm text-slate-700">Permite multipla selecao</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.ativo}
                      onChange={(e) => setFormData({...formData, ativo: e.target.checked})}
                      className="w-4 h-4 text-teal-600 rounded border-slate-300 focus:ring-teal-500"
                      data-testid="proc-ativo-checkbox"
                    />
                    <span className="text-sm text-slate-700">Ativo</span>
                  </label>
                </div>

                <label className="flex items-center gap-2 cursor-pointer p-2 rounded-lg bg-amber-50 border border-amber-200">
                  <input
                    type="checkbox"
                    checked={formData.somente_admin_geral}
                    onChange={(e) => setFormData({...formData, somente_admin_geral: e.target.checked})}
                    className="w-4 h-4 text-amber-600 rounded border-amber-300 focus:ring-amber-500"
                    data-testid="proc-admin-geral-only-checkbox"
                  />
                  <div>
                    <span className="text-sm font-medium text-amber-800">Somente Admin Geral</span>
                    <p className="text-xs text-amber-600">Apenas o Administrador Geral pode marcar e cancelar este procedimento</p>
                  </div>
                </label>
              </div>

              {isLabMunSigla(formData.sigla) && (
                <div className="p-3 rounded-lg bg-purple-50 border border-purple-200">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm font-medium text-purple-900">
                      Dias da semana permitidos para marcacao
                    </Label>
                    <span className="text-xs text-purple-600">(Lab. Municipal)</span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap" data-testid="dias-semana-checkboxes">
                    {[
                      { id: 0, label: 'D', full: 'Domingo' },
                      { id: 1, label: 'S', full: 'Segunda' },
                      { id: 2, label: 'T', full: 'Terca' },
                      { id: 3, label: 'Q', full: 'Quarta' },
                      { id: 4, label: 'Q', full: 'Quinta' },
                      { id: 5, label: 'S', full: 'Sexta' },
                      { id: 6, label: 'S', full: 'Sabado' },
                    ].map((dia) => {
                      const checked = (formData.dias_semana || []).includes(dia.id);
                      return (
                        <label
                          key={dia.id}
                          className={`flex flex-col items-center justify-center w-12 h-12 rounded-lg border-2 cursor-pointer transition-colors ${
                            checked
                              ? 'bg-purple-600 border-purple-600 text-white'
                              : 'bg-white border-slate-300 text-slate-600 hover:border-purple-300'
                          }`}
                          title={dia.full}
                          data-testid={`dia-semana-${dia.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleDiaSemana(dia.id)}
                            className="sr-only"
                          />
                          <span className="text-xs font-bold">{dia.label}</span>
                          <span className="text-[10px] opacity-75">{dia.full.slice(0, 3)}</span>
                        </label>
                      );
                    })}
                  </div>
                  <p className="text-xs text-purple-700 mt-2">
                    Por padrao todos os dias estao marcados. Desmarque para restringir (ex: BAAR DE ESCARRO so SEG/QUA/SEX).
                  </p>
                </div>
              )}

              <div>
                <Label htmlFor="preparo">Preparos / Orientacoes ao Paciente</Label>
                <textarea
                  id="preparo"
                  value={formData.preparo}
                  onChange={(e) => setFormData({...formData, preparo: e.target.value.toUpperCase()})}
                  placeholder="EX: LEVAR DOCUMENTOS, ESTAR EM JEJUM, ETC."
                  className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-y min-h-[80px] uppercase"
                  rows={3}
                  data-testid="proc-preparo-input"
                />
                <p className="text-xs text-slate-400 mt-1">Estas informações aparecerão no cartão de confirmação do paciente</p>
              </div>

              <div className="flex gap-3 pt-4 border-t border-slate-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowModal(false)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-teal-700 hover:bg-teal-800"
                  data-testid="save-proc-button"
                >
                  <Check className="h-4 w-4 mr-2" />
                  {editingProc ? 'Atualizar' : 'Cadastrar'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
